import React, { useState } from "react";
import { ArrowLeft, BrickWall, Check, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAppSelector } from "../store/hooks";
import { BrickSeries, selectBricksLoading, selectSeriesList } from "../store/slices/bricksSlice";
import { selectSelectedBricks } from "../store/slices/configurationSlice";
import { matchesName } from "../search/matchesName";

interface Props {
  onSelect(b: BrickSeries): void;
  onBack(): void;
}

const BrickPicker: React.FC<Props> = ({ onSelect, onBack }) => {
  const seriesList = useAppSelector(selectSeriesList);
  const selectedBricks = useAppSelector(selectSelectedBricks);
  const loading = useAppSelector(selectBricksLoading);
  const [query, setQuery] = useState("");
  const visibleSeries = seriesList.filter((b) => matchesName(b.name, query));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b p-3">
        <Button variant="ghost" size="icon" onClick={onBack} aria-label="Terug">
          <ArrowLeft />
        </Button>
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            placeholder="Zoek een steen…"
            aria-label="Zoek een steen"
            // The picker opens to search, so the search box takes focus.
            // eslint-disable-next-line jsx-a11y-x/no-autofocus
            autoFocus
            className="pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && !query && onBack()}
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {visibleSeries.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            {query
              ? "Geen stenen gevonden"
              : loading
                ? "Stenen laden…"
                : "Er zijn nog geen stenen."}
          </p>
        ) : (
          <ul className="grid grid-cols-2 gap-3">
            {visibleSeries.map((b) => {
              const selected = Object.prototype.hasOwnProperty.call(selectedBricks, b._id);
              return (
                <li key={b._id}>
                  <button
                    type="button"
                    onClick={() => onSelect(b)}
                    aria-pressed={selected}
                    className={cn(
                      "group relative w-full overflow-hidden rounded-lg border bg-card text-left outline-none transition-shadow hover:shadow-md focus-visible:ring-[3px] focus-visible:ring-ring/50",
                      selected && "border-primary ring-1 ring-primary",
                    )}
                  >
                    {b.displayThumbnail ? (
                      <img
                        src={b.displayThumbnail}
                        alt=""
                        loading="lazy"
                        className="aspect-4/3 w-full bg-muted object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                      />
                    ) : (
                      <span className="flex aspect-4/3 w-full items-center justify-center bg-muted text-muted-foreground">
                        <BrickWall className="size-6" />
                      </span>
                    )}
                    <span className="block truncate border-t px-2.5 py-2 text-sm font-medium">
                      {b.name}
                    </span>
                    {selected && (
                      <span className="absolute top-2 right-2 flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                        <Check className="size-3.5" />
                        <span className="sr-only">Gekozen</span>
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default BrickPicker;
